import { useState } from "react";
import ImageUploader from "./ImageUploader";
import GalleryEditor from "./GalleryEditor";
import PdfUploader, { formatBytes } from "./PdfUploader";
import { Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { resolveImage } from "../../lib/data";
import { toast } from "sonner";
import { adminApi } from "../api";

/**
 * Generic entity editor used by Hotels, Openings, News, Documents.
 *
 * Props:
 *   title: page title
 *   endpoint: "/admin/hotels" etc.
 *   fields: [{ key, label, type, required, placeholder, options, textarea, imageField, list }]
 *   renderRow: (item) => ReactNode
 *   initial: default new-item values
 *   items / setItems / onReload
 */
export default function EntityEditor({
  title,
  subtitle,
  endpoint,
  fields,
  items,
  initial,
  onReload,
  renderRow,
  imageField = "image",
}) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setForm(initial);
    setEditing("new");
  };

  const openEdit = (item) => {
    setForm({ ...initial, ...item });
    setEditing(item.id);
  };

  const close = () => {
    setEditing(null);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // ensure list fields stored as array
      fields.forEach((f) => {
        if (f.list && typeof payload[f.key] === "string") {
          payload[f.key] = payload[f.key]
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean);
        }
        if (f.type === "number") {
          const v = payload[f.key];
          if (f.nullable && (v === "" || v === null || v === undefined)) {
            payload[f.key] = null;
          } else {
            const n = Number(v);
            payload[f.key] = Number.isFinite(n) ? n : (f.nullable ? null : 0);
          }
        }
        if (f.type === "checkbox") {
          payload[f.key] = !!payload[f.key];
        }
      });
      if (editing === "new") {
        await adminApi.post(endpoint, payload);
        toast.success("Creato");
      } else {
        await adminApi.put(`${endpoint}/${editing}`, payload);
        toast.success("Aggiornato");
      }
      close();
      onReload();
    } catch (err) {
      toast.error("Salvataggio fallito", {
        description: err.response?.data?.detail || "Controlla i campi",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    try {
      await adminApi.delete(`${endpoint}/${id}`);
      toast.success("Eliminato");
      onReload();
    } catch (err) {
      toast.error("Eliminazione fallita");
    }
  };

  return (
    <div data-testid={`admin-${endpoint.split("/").pop()}-page`}>
      <div className="flex items-start justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
            CMS
          </div>
          <h1 className="mt-2 font-display font-light text-5xl tracking-tightest">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 text-white/60 max-w-xl">{subtitle}</p>
          )}
        </div>
        <button
          onClick={openNew}
          data-testid={`admin-${endpoint.split("/").pop()}-new`}
          className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm hover:bg-[#C48F56]"
        >
          <Plus size={14} /> Nuovo
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0F0F14] divide-y divide-white/5">
        {items.length === 0 && (
          <div className="p-10 text-center text-white/40">
            Nessun elemento. Clicca "Nuovo" per iniziare.
          </div>
        )}
        {items.map((item) => (
          <div
            key={item.id}
            className="p-5 flex items-center gap-5"
            data-testid={`admin-row-${item.id}`}
          >
            {imageField && item[imageField] && (
              <img
                src={resolveImage(item[imageField])}
                alt=""
                className="h-14 w-20 rounded-md object-cover border border-white/10"
                onError={(e) => (e.currentTarget.style.opacity = "0.3")}
              />
            )}
            <div className="flex-1 min-w-0">{renderRow(item)}</div>
            <div className="flex gap-2">
              <button
                onClick={() => openEdit(item)}
                data-testid={`admin-edit-${item.slug || item.id}`}
                className="h-9 w-9 rounded-full border border-white/10 grid place-items-center hover:bg-white/5"
                aria-label="Modifica"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => remove(item.id)}
                data-testid={`admin-delete-${item.slug || item.id}`}
                className="h-9 w-9 rounded-full border border-white/10 grid place-items-center hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30"
                aria-label="Elimina"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <FormModal
          title={editing === "new" ? `Crea ${title}` : `Modifica ${title}`}
          onClose={close}
          fields={fields}
          form={form}
          setForm={setForm}
          onSubmit={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function FormModal({ title, onClose, fields, form, setForm, onSubmit, saving }) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-6 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-form-modal"
        className="relative w-full max-w-2xl bg-[#0A0A0D] border border-white/10 rounded-2xl p-8 my-10"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full border border-white/10 grid place-items-center hover:bg-white/5"
            aria-label="Chiudi"
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
          {fields.map((f) => (
            <FieldRenderer key={f.key} f={f} form={form} setForm={setForm} />
          ))}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-white/15 px-5 py-2 text-sm"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-form-submit"
            className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2 text-sm font-medium hover:bg-[#C48F56] disabled:opacity-50"
          >
            {saving ? "Salvataggio…" : "Salva"}
            <Save size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldRenderer({ f, form, setForm }) {
  const val = form[f.key] ?? "";
  const set = (v) => setForm((s) => ({ ...s, [f.key]: v }));

  if (f.type === "divider") {
    return (
      <div className="pt-6 mt-2 border-t border-[#C48F56]/30">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C48F56]">
          {f.label}
        </div>
      </div>
    );
  }

  if (f.type === "image") {
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
          {f.label}
        </label>
        <ImageUploader value={val} onChange={set} testId={`field-${f.key}`} />
      </div>
    );
  }

  if (f.type === "pdf") {
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
          {f.label}
        </label>
        <PdfUploader
          value={val}
          onChange={set}
          onSizeChange={(bytes, filename) => {
            // Auto-populate companion fields when present
            setForm((s) => ({
              ...s,
              [`${f.key}_size_bytes`]: bytes,
              ...(f.autoFillSize ? { [f.autoFillSize]: formatBytes(bytes) } : {}),
              ...(filename && f.autoFillFilename
                ? { [f.autoFillFilename]: filename }
                : {}),
            }));
          }}
          testId={`field-${f.key}`}
        />
      </div>
    );
  }

  if (f.type === "gallery") {
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
          {f.label}
        </label>
        <GalleryEditor
          value={Array.isArray(val) ? val : []}
          onChange={set}
          testId={`field-${f.key}`}
        />
      </div>
    );
  }

  if (f.textarea) {
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
          {f.label}
        </label>
        <textarea
          value={f.list ? (Array.isArray(val) ? val.join("\n") : val) : val}
          onChange={(e) =>
            set(
              f.list
                ? e.target.value
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : e.target.value
            )
          }
          rows={f.rows || 4}
          placeholder={f.placeholder}
          required={f.required}
          data-testid={`field-${f.key}`}
          className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#C48F56] outline-none resize-y"
        />
        {f.list && (
          <p className="mt-1 text-[11px] text-white/40">
            Un paragrafo per riga.
          </p>
        )}
      </div>
    );
  }

  if (f.type === "select") {
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
          {f.label}
        </label>
        <select
          value={val}
          onChange={(e) => set(e.target.value)}
          required={f.required}
          data-testid={`field-${f.key}`}
          className="w-full bg-[#0A0A0D] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#C48F56] outline-none"
        >
          {f.options.map((o) => (
            <option key={o.value ?? o} value={o.value ?? o}>
              {o.label ?? o}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (f.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!val}
          onChange={(e) => set(e.target.checked)}
          data-testid={`field-${f.key}`}
          className="h-4 w-4 accent-[#C48F56]"
        />
        <span className="text-sm">{f.label}</span>
      </label>
    );
  }

  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
        {f.label}
      </label>
      <input
        type={f.type || "text"}
        step={f.step}
        value={val ?? ""}
        onChange={(e) => set(e.target.value)}
        required={f.required}
        placeholder={f.placeholder}
        data-testid={`field-${f.key}`}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
      />
    </div>
  );
}
